import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import {
  Button,
  ButtonAppearance,
  Heading,
  Text,
  Icon,
  Link,
  LinkSize,
  LinkStyle,
  MeatballMenu,
} from '@atlassian/learning-components';

import { formatTimeRange, ISODateTimeString } from 'src/utils/date';

import { BUTTON_LABELS, TEXT_LABELS } from './LiveTrainingCard';

export interface LiveTrainingCardMobileProps {
  title: string;
  startDateTime: ISODateTimeString;
  endDateTime: ISODateTimeString;
  isInPast: boolean;
  isCompleted: boolean;
  enrolled: boolean;
  isFree: boolean;
  isSaved: boolean;
  host: string;
  videoCallUrl?: string;
  isCompletionCertificateLoading?: boolean;
  showDownloadCompletionCertificate?: boolean;
  isCard?: boolean;

  // Handlers
  onDetailsClick?: () => void;
  onSave?: () => void;
  onDownloadCompletionCertificate?: () => void;
  onJoinViaVideoCall?: () => void;

  // Meatball menu
  meatballMenuItems: Array<{ label: string; onClick: () => void }>;

  // Tags
  tags: React.ReactNode[];
}

export const LiveTrainingCardMobile = ({
  title,
  startDateTime,
  endDateTime,
  isInPast,
  isCompleted,
  enrolled,
  host,
  isCompletionCertificateLoading = false,
  showDownloadCompletionCertificate = false,
  isCard = true,
  onDetailsClick,
  onDownloadCompletionCertificate,
  onJoinViaVideoCall,
  meatballMenuItems,
  tags,
}: LiveTrainingCardMobileProps) => {
  const formattedDateTime = formatTimeRange(startDateTime, endDateTime);
  const hasMeatballMenuItems = meatballMenuItems.length > 0;

  // Format date as "OCT 21"
  const date = new Date(startDateTime);
  const formattedDate = date
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    .toUpperCase()
    .replace(',', '');

  const handleDetailsClick = (event: React.MouseEvent): void => {
    if (onDetailsClick) {
      event.preventDefault();
      onDetailsClick();
    }
  };

  const showCompletionCertificateButton =
    isCompleted && showDownloadCompletionCertificate && onDownloadCompletionCertificate;
  const showJoinViaVideoCallButton = enrolled && !isInPast && onJoinViaVideoCall;
  const showButtons = showCompletionCertificateButton || showJoinViaVideoCallButton;

  return (
    <article data-testid="live-training-card-mobile" css={containerStyles}>
      <div css={contentStyles}>
        <div css={timeRowStyles}>
          <div css={timeRowContentStyles}>
            <Icon type="calendar" color={token('color.text.brand')} />
            <div css={css({ display: 'flex', flexDirection: 'column' })}>
              <Text color={token('color.text.brand')}>{formattedDate}</Text>
              {isInPast ? <Text>{TEXT_LABELS.PAST}</Text> : <Text>{formattedDateTime}</Text>}
            </div>
          </div>
          {hasMeatballMenuItems && <MeatballMenu items={meatballMenuItems} />}
        </div>

        <div css={titleContainerStyles}>
          {!isCard ? (
            <div css={titleStyles} onClick={handleDetailsClick}>
              <Heading level={4}>{title}</Heading>
            </div>
          ) : (
            <Link
              link="#"
              label={title}
              size={LinkSize.EXTRA_LARGE}
              style={LinkStyle.SEMIBOLD}
              target="_self"
              onClick={handleDetailsClick}
              css={titleLinkStyles}
            />
          )}
        </div>

        <div css={metadataRowStyles}>
          <div css={tagListStyles}>{tags}</div>
          <div css={hostStyles}>
            <Icon type="person" size="small" color={token('color.icon.brand')} />
            <Text size="small">{host}</Text>
          </div>
        </div>
      </div>

      {/* Mobile button row - only show primary actions */}
      {showButtons && (
        <div css={buttonRowStyles}>
          {showCompletionCertificateButton && (
            <Button
              label={BUTTON_LABELS.DOWNLOAD_COMPLETION_CERTIFICATE}
              appearance={ButtonAppearance.PRIMARY}
              onClick={onDownloadCompletionCertificate}
              isLoading={isCompletionCertificateLoading}
            />
          )}
          {showJoinViaVideoCallButton && (
            <Button
              label={BUTTON_LABELS.JOIN_VIDEO_CALL}
              appearance={ButtonAppearance.PRIMARY}
              iconAfter={<Icon type="link-external" color={token('color.icon.inverse')} />}
              onClick={onJoinViaVideoCall}
            />
          )}
        </div>
      )}
    </article>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  padding: `${token('space.200')}`,
  borderBottom: `1px solid ${token('color.border')}`,
});

const contentStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.050'),
});

const timeRowStyles = css({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const timeRowContentStyles = css({
  display: 'flex',
  flexDirection: 'row',
  gap: token('space.050'),
  alignItems: 'baseline',

  '& svg': {
    color: token('color.icon.brand'),
  },
});

const titleContainerStyles = css({
  '& span:first-of-type': {
    verticalAlign: 'middle',
  },

  '& a': {
    color: token('color.link'),
    fontFamily: 'Charlie Display, sans-serif',
  },

  '& a:hover': {
    textDecoration: 'none',
    color: token('color.link.pressed'),
  },

  '& span': {
    fontFamily: 'Charlie Display, sans-serif',
    fontSize: '24px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '32px',
  },
});

const titleStyles = css({
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const titleLinkStyles = css({
  '& svg': {
    display: 'none',
  },
});

const metadataRowStyles = css({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginTop: token('space.200'),
});

const tagListStyles = css({
  display: 'flex',
  flexDirection: 'row',
  gap: token('space.100'),
  flexWrap: 'wrap',
});

const hostStyles = css({
  display: 'flex',
  flexDirection: 'row',
  gap: token('space.100'),
  alignItems: 'center',
  minWidth: 'fit-content',

  '& svg': {
    color: token('color.icon.brand'),
  },
});

const buttonRowStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.100'),
  marginTop: token('space.200'),
  width: '100%',
  alignItems: 'flex-end',

  '& button': {
    width: '100%',
  },

  '& svg': {
    color: token('color.icon.inverse'),
  },
});
